import { useEffect, useMemo, useRef, useState } from 'react';
import { Results } from '@atlassian/cssxp-typescommon';
import { useFetch } from './use-fetch';

const loadMoreSize = 50;

type useSearchArgs<T> = {
  fetchSearchResults: (...searchParams: any[]) => Promise<Results<T>>;
  params: Record<string, any>;
};

type useSearchResponse<T> = {
  isLoadMoreEnabled: boolean;
  isSearching: boolean;
  searchResults: Results<T>['results'];
  completeSearchResponse?: Record<string, any>;
  totalHits: number;
  error: any;
  handleLoadMore: () => void;
};

const initialPageConfig = {
  from: 0,
  size: 15,
};

export const useSearch = <T>({ fetchSearchResults, params }: useSearchArgs<T>): useSearchResponse<T> => {
  const [pageConfig, setPageConfig] = useState<{
    from: number;
    size: number;
  }>({
    from: initialPageConfig.from,
    size: initialPageConfig.size,
  });
  const [searchResults, setSearchResults] = useState<T[]>([]);

  const startPageNumber = useRef(initialPageConfig.from);

  const handleLoadMore = () => {
    const offset = searchResults.length;

    setPageConfig((prev) => ({
      ...prev,
      from: offset,
      size: loadMoreSize,
    }));
  };

  useEffect(() => {
    // search params updated by user. reset page.

    // page config is same as before. bail out without setting.
    if (pageConfig.size === initialPageConfig.size && pageConfig.from === initialPageConfig.from) {
      return;
    }

    setPageConfig((prev) => ({
      ...prev,
      from: 0,
      size: 15,
    }));
    startPageNumber.current = 0;

    // Disabling exhaustive check as we need the effect to run ONLY when `params` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const searchParams = useMemo(() => {
    const updatedParams = {
      ...pageConfig,
      ...params,
    };

    return updatedParams;
  }, [pageConfig, params]);

  const { isLoading: isSearching, errors: error, data: searchResponse } = useFetch({
    api: ([latestSearchParams]) => fetchSearchResults(latestSearchParams),
    deps: [searchParams],
  });

  const results = useMemo(() => searchResponse?.results || [], [searchResponse?.results]);

  const totalHits = searchResponse?.totalHits || 0;

  useEffect(() => {
    const { from } = pageConfig;

    if (isSearching) return;

    if (startPageNumber.current === from) {
      // page number did not change. only new instance of results
      setSearchResults(results);
    } else {
      // page number changed. append new results to existing
      setSearchResults((prev: T[]) => {
        startPageNumber.current = from;

        const updatedSearchResults = [...prev, ...results];

        return updatedSearchResults;
      });
    }
    // Disabling exhaustive check as we need the effect to run ONLY when `results` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const isLoadMoreEnabled = useMemo(() => {
    return !isSearching && searchResults.length < totalHits;
  }, [isSearching, searchResults.length, totalHits]);

  const searchResponseWithUpdatedResultList = useMemo(
    () => ({
      ...searchResponse,
      results: searchResults,
    }),
    [searchResponse, searchResults]
  );

  return {
    isLoadMoreEnabled,
    isSearching,
    searchResults,
    totalHits,
    error,
    handleLoadMore,

    completeSearchResponse: searchResponseWithUpdatedResultList,
  };
};
